import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Upload, Download, FileText, AlertCircle, CheckCircle,
  Database, ChevronLeft, ChevronRight, Calendar, Filter
} from 'lucide-react';
import { leadsAPI, usersAPI } from '../services/api';
import { formatCurrency, formatStatusLabel } from '../utils/formatters';

const STEPS = [
  { num: 1, label: 'Action' },
  { num: 2, label: 'Source' },
  { num: 3, label: 'Proceed' },
];

const SOURCE_OPTIONS = [
  {
    key: 'general',
    label: 'General Leads',
    description: 'All general inquiries, referrals, walk-ins, and website leads',
    icon: Database,
  },
  {
    key: 'meta_ads',
    label: 'Meta Ads',
    description: 'Leads generated from Facebook and Instagram Lead Ads',
    icon: Database,
  },
  {
    key: 'eighteen',
    label: 'Eighteen',
    description: 'Project-specific leads for the Eighteen development',
    icon: Database,
  },
];

const STATUS_OPTIONS_DEFAULT = ['new', 'contacted', 'in_progress', 'follow_up', 'negotiating', 'won', 'lost', 'queued_unassigned', 'archived'];
const EXPORT_FIELD_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'property_type', label: 'Property Type' },
  { value: 'budget', label: 'Budget' },
  { value: 'status', label: 'Status' },
  { value: 'source', label: 'Source' },
  { value: 'location', label: 'Location' },
  { value: 'assigned_to', label: 'Assigned To' },
  { value: 'created_date', label: 'Created Date' },
  { value: 'notes', label: 'Notes' },
];

const SOURCE_FILTER_MAP = {
  general: 'General',
  meta_ads: 'Meta Ads',
  eighteen: 'Eighteen',
};

function getGuidance(mode, sourceKey) {
  if (mode === 'import') {
    switch (sourceKey) {
      case 'general':
        return [
          'Download our template to see the correct format',
          'Required: Name, Phone number',
          'Optional: Email, Location, Budget, Notes',
          'Upload your Excel (.xlsx) or CSV file below',
        ];
      case 'meta_ads':
        return [
          'Meta Ads leads come from Facebook Lead Ads',
          'Your file should have: Full Name, Phone Number',
          'The system will automatically label these as Meta Ads',
          'Upload your Excel (.xlsx) or CSV file below',
        ];
      case 'eighteen':
        return [
          'Eighteen leads are project-specific imports',
          'Your file should have: Purchaser Name, Phone, Email',
          'Optional: CNIC/Passport, Unit Category, Unit Type',
          'Upload your Excel (.xlsx) or CSV file below',
        ];
      default:
        return [];
    }
  }
  return [
    'Use the filters below to narrow down which leads to export',
    'Source is pre-selected based on your choice',
    'Preview the count before exporting',
    'Download as CSV or Excel',
  ];
}

const DataTransferModal = ({ isOpen, onClose, onImportSuccess, initialMode = 'import', initialSource = null }) => {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(initialMode);
  const [source, setSource] = useState(initialSource || 'general');

  useEffect(() => {
    if (isOpen) {
      setStep(initialSource ? 2 : 1);
      setMode(initialMode);
      setSource(initialSource || 'general');
      resetImportState();
      resetExportState();
    }
  }, [isOpen, initialMode, initialSource]);

  // ─── IMPORT STATE ──────────────────────────────────────────
  const [importStep, setImportStep] = useState('upload');
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  const resetImportState = () => {
    setImportStep('upload');
    setSelectedFile(null);
    setValidationResults(null);
    setImportResults(null);
    setDragActive(false);
    setImportLoading(false);
  };

  // ─── EXPORT STATE ──────────────────────────────────────────
  const [exportFilters, setExportFilters] = useState({
    dateFrom: '',
    dateTo: '',
    datePreset: 'all',
    statuses: [],
    locations: '',
    budgetMin: '',
    budgetMax: '',
    sources: [],
    assignedTo: '',
  });
  const [exportOptions, setExportOptions] = useState({
    format: 'csv',
    includeFields: ['name', 'email', 'phone', 'property_type', 'budget', 'status', 'source', 'assigned_to', 'created_date'],
  });
  const [exportPreview, setExportPreview] = useState({ count: 0, leads: [] });
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportConfirmation, setShowExportConfirmation] = useState(false);
  const [users, setUsers] = useState([]);
  const [statusOptions] = useState(STATUS_OPTIONS_DEFAULT);

  const resetExportState = () => {
    setExportFilters({ dateFrom: '', dateTo: '', datePreset: 'all', statuses: [], locations: '', budgetMin: '', budgetMax: '', sources: [], assignedTo: '' });
    setExportOptions({ format: 'csv', includeFields: ['name', 'email', 'phone', 'property_type', 'budget', 'status', 'source', 'assigned_to', 'created_date'] });
    setExportPreview({ count: 0, leads: [] });
    setExportLoading(false);
    setShowExportConfirmation(false);
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await usersAPI.getAll();
      setUsers(res.data || []);
    } catch {
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    if (isOpen && mode === 'export') fetchUsers();
  }, [isOpen, mode, fetchUsers]);

  const assignableUsers = users.filter(
    (u) => String(u?.role || '').trim().toLowerCase() === 'sales_agent' && u?.is_active !== false
  );

  // ─── IMPORT LOGIC ──────────────────────────────────────────
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && isValidFileType(file)) setSelectedFile(file);
    else alert('Please upload a valid Excel or CSV file (.xlsx, .xls, .csv, .ods)');
  };

  const isValidFileType = (file) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'application/vnd.oasis.opendocument.spreadsheet',
    ];
    return validTypes.includes(file.type) || /\.(xlsx?|csv|ods)$/i.test(file.name);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && isValidFileType(file)) setSelectedFile(file);
    else alert('Please select a valid Excel or CSV file (.xlsx, .xls, .csv, .ods)');
  };

  const downloadTemplate = () => {
    leadsAPI.downloadTemplate().then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'lead_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    }).catch((error) => {
      console.error('Failed to download template:', error);
      alert('Failed to download template');
    });
  };

  const validateAndPreview = async () => {
    if (!selectedFile) { alert('Please select a file'); return; }
    setImportLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const response = await leadsAPI.validateImport(formData);
      setValidationResults(response.data);
      setImportStep('validation');
    } catch (error) {
      console.error('Validation failed:', error);
      alert(error.response?.data?.error || 'File validation failed');
    } finally {
      setImportLoading(false);
    }
  };

  const proceedWithImport = async () => {
    if (!validationResults) return;
    setImportLoading(true);
    try {
      const response = await leadsAPI.importLeads({
        file_data: validationResults.parsed_data,
        skip_invalid: true,
        source: SOURCE_FILTER_MAP[source] || 'Eighteen',
      });
      setImportResults(response.data);
      setImportStep('results');
    } catch (error) {
      console.error('Import failed:', error);
      alert(error.response?.data?.error || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  const downloadErrorReport = async () => {
    if (!importResults?.error_report) return;
    try {
      const response = await leadsAPI.downloadErrorReport({ error_data: importResults.error_report });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'lead_import_errors.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Failed to download error report:', error);
      alert('Failed to download error report');
    }
  };

  const handleImportComplete = () => {
    onImportSuccess?.();
    handleClose();
  };

  // ─── EXPORT LOGIC ──────────────────────────────────────────
  const buildExportFilters = () => {
    const filters = {};
    if (exportFilters.dateFrom?.trim()) filters.date_from = exportFilters.dateFrom.trim();
    if (exportFilters.dateTo?.trim()) filters.date_to = exportFilters.dateTo.trim();
    if (exportFilters.statuses.length > 0) filters.statuses = exportFilters.statuses.join(',');
    if (exportFilters.locations?.trim()) filters.location = exportFilters.locations.trim();
    const budgetMin = parseFloat(exportFilters.budgetMin);
    if (!isNaN(budgetMin) && budgetMin >= 0) filters.budget_min = budgetMin;
    const budgetMax = parseFloat(exportFilters.budgetMax);
    if (!isNaN(budgetMax) && budgetMax >= 0) filters.budget_max = budgetMax;
    if (exportFilters.sources.length > 0) filters.sources = exportFilters.sources.join(',');
    if (exportFilters.assignedTo?.trim()) filters.assigned_to_id = exportFilters.assignedTo.trim();
    return filters;
  };

  const applyDatePreset = (preset) => {
    const today = new Date();
    if (preset === 'all') {
      setExportFilters({ ...exportFilters, datePreset: 'all', dateFrom: '', dateTo: '' });
      return;
    }
    let fromDate = new Date(today);
    switch (preset) {
      case 'today': fromDate = new Date(today); break;
      case '7days': fromDate.setDate(today.getDate() - 7); break;
      case '30days': fromDate.setDate(today.getDate() - 30); break;
      case 'quarter': fromDate.setMonth(today.getMonth() - 3); break;
      case 'year': fromDate.setFullYear(today.getFullYear() - 1); break;
      default: return;
    }
    setExportFilters({
      ...exportFilters,
      datePreset: preset,
      dateFrom: fromDate.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0],
    });
  };

  const loadExportPreview = async () => {
    try {
      const filters = buildExportFilters();
      const response = await leadsAPI.exportLeads({
        ...filters,
        format: 'json',
        preview: true,
        limit: 10,
      });
      setExportPreview({
        count: response.data?.total_count || 0,
        leads: response.data?.leads || [],
      });
    } catch (error) {
      console.error('Failed to load export preview:', error);
      setExportPreview({ count: 0, leads: [] });
    }
  };

  useEffect(() => {
    if (step === 3 && mode === 'export') {
      loadExportPreview();
    }
  }, [step, mode, exportFilters]);

  const getAppliedFiltersSummary = () => {
    const appliedFilters = [];
    if (exportFilters.dateFrom || exportFilters.dateTo) {
      if (exportFilters.datePreset !== 'all') {
        const labels = { today: 'Today', '7days': 'Last 7 Days', '30days': 'Last 30 Days', quarter: 'Last Quarter', year: 'Last Year' };
        appliedFilters.push(`Date: ${labels[exportFilters.datePreset]}`);
      } else if (exportFilters.dateFrom && exportFilters.dateTo) {
        appliedFilters.push(`Date: ${exportFilters.dateFrom} to ${exportFilters.dateTo}`);
      }
    }
    if (exportFilters.statuses.length > 0) {
      appliedFilters.push(`Status: ${exportFilters.statuses.map(s => formatStatusLabel(s)).join(', ')}`);
    }
    if (exportFilters.locations) appliedFilters.push(`Location: ${exportFilters.locations}`);
    if (exportFilters.budgetMin || exportFilters.budgetMax) {
      appliedFilters.push(`Budget: ${exportFilters.budgetMin ? formatCurrency(exportFilters.budgetMin) : '0'} - ${exportFilters.budgetMax ? formatCurrency(exportFilters.budgetMax) : '∞'}`);
    }
    if (exportFilters.sources.length > 0) appliedFilters.push(`Source: ${exportFilters.sources.join(', ')}`);
    if (exportFilters.assignedTo) {
      const user = users.find(u => u.id === exportFilters.assignedTo);
      const label = exportFilters.assignedTo === 'unassigned' ? 'Unassigned' : (user?.full_name || user?.username || 'Unknown');
      appliedFilters.push(`Assigned To: ${label}`);
    }
    return appliedFilters;
  };

  const handleExportClick = () => {
    if (exportPreview.count === 0) { alert('No leads match the selected filters'); return; }
    setShowExportConfirmation(true);
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const filters = buildExportFilters();
      const params = {
        format: exportOptions.format,
        limit: 50000,
        include_fields: exportOptions.includeFields.join(','),
        ...filters,
      };
      const response = await leadsAPI.exportLeads(params);
      const blob = new Blob([response.data], {
        type: exportOptions.format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().split('T')[0];
      a.download = `leads-export-${today}.${exportOptions.format === 'excel' ? 'xlsx' : 'csv'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      setShowExportConfirmation(false);
      handleClose();
    } catch (err) {
      console.error('Export failed:', err);
      alert(err.response?.data?.error || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handleClose = () => {
    resetImportState();
    resetExportState();
    setStep(1);
    onClose();
  };

  if (!isOpen) return null;

  // ─── STEP 1: ACTION SELECTION ──────────────────────────────
  const renderStep1 = () => (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">Choose what you want to do with your lead data</p>
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => { setMode('import'); setStep(2); }}
          className={`relative flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all ${
            mode === 'import'
              ? 'border-gray-900 bg-gray-50 shadow-sm'
              : 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50'
          }`}
        >
          <div className={`p-3 rounded-full ${mode === 'import' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
            <Upload size={28} />
          </div>
          <div className="text-center">
            <p className="font-semibold text-gray-900">Import</p>
            <p className="text-xs text-gray-500 mt-1">Bring leads in from a file</p>
          </div>
          {mode === 'import' && (
            <span className="absolute top-2 right-2 w-3 h-3 bg-gray-900 rounded-full ring-2 ring-white" />
          )}
        </button>
        <button
          onClick={() => { setMode('export'); setStep(2); }}
          className={`relative flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all ${
            mode === 'export'
              ? 'border-gray-900 bg-gray-50 shadow-sm'
              : 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50'
          }`}
        >
          <div className={`p-3 rounded-full ${mode === 'export' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
            <Download size={28} />
          </div>
          <div className="text-center">
            <p className="font-semibold text-gray-900">Export</p>
            <p className="text-xs text-gray-500 mt-1">Download leads to a file</p>
          </div>
          {mode === 'export' && (
            <span className="absolute top-2 right-2 w-3 h-3 bg-gray-900 rounded-full ring-2 ring-white" />
          )}
        </button>
      </div>
    </div>
  );

  // ─── STEP 2: SOURCE SELECTION ──────────────────────────────
  const renderStep2 = () => (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        {mode === 'import' ? 'Which type of leads are you importing?' : 'Which leads do you want to export?'}
      </p>
      <div className="space-y-3">
        {SOURCE_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            onClick={() => { setSource(opt.key); setStep(3); }}
            className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
              source === opt.key
                ? 'border-gray-900 bg-gray-50 shadow-sm'
                : 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50'
            }`}
          >
            <div className={`p-2.5 rounded-lg ${source === opt.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
              <opt.icon size={22} />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900">{opt.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>
            </div>
            {source === opt.key && (
              <span className="w-3 h-3 bg-gray-900 rounded-full ring-2 ring-white flex-shrink-0" />
            )}
          </button>
        ))}
      </div>
    </div>
  );

  // ─── STEP 3: IMPORT UPLOAD ─────────────────────────────────
  const renderImport = () => {
    const guidance = getGuidance('import', source);

    if (importStep === 'upload') {
      return (
        <div className="space-y-5">
          {/* Guidance */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 text-sm mb-2">How to prepare your data</h3>
            <ul className="space-y-1">
              {guidance.map((item, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-700">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Template Download */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Need a template?</p>
              <p className="text-xs text-gray-500">Download our sample file to see the correct format</p>
            </div>
            <button onClick={downloadTemplate} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <Download size={16} />
              Template
            </button>
          </div>

          {/* File Upload */}
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragActive ? 'border-gray-900 bg-gray-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="mx-auto mb-3 text-gray-400" size={36} />
            <p className="font-medium text-gray-900 mb-1">Drag and drop your file here</p>
            <p className="text-sm text-gray-500 mb-4">or</p>
            <button onClick={() => fileInputRef.current?.click()} className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-black transition-colors">
              Select File
            </button>
            <input ref={fileInputRef} type="file" onChange={handleFileSelect} accept=".xlsx,.xls,.csv,.ods" className="hidden" />
            <p className="text-xs text-gray-400 mt-4">Supported: Excel (.xlsx, .xls, .ods) and CSV</p>
          </div>

          {selectedFile && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-3">
              <FileText className="text-gray-600" size={20} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(2)} KB</p>
              </div>
              <button onClick={() => setSelectedFile(null)} className="text-gray-400 hover:text-gray-700 p-1">
                <X size={16} />
              </button>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={() => setStep(2)} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5">
              <ChevronLeft size={16} /> Back
            </button>
            <button
              onClick={validateAndPreview}
              disabled={!selectedFile || importLoading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
            >
              {importLoading ? 'Validating...' : 'Validate & Preview'} {!importLoading && <ChevronRight size={16} />}
            </button>
          </div>
        </div>
      );
    }

    if (importStep === 'validation' && validationResults) {
      return (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{validationResults.total_rows}</p>
              <p className="text-xs text-gray-500">Total Rows</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{validationResults.valid_rows}</p>
              <p className="text-xs text-gray-500">Valid Rows</p>
            </div>
            <div className="bg-red-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{validationResults.invalid_rows}</p>
              <p className="text-xs text-gray-500">Invalid Rows</p>
            </div>
          </div>

          {validationResults.invalid_rows > 0 && validationResults.errors && (
            <div className="border border-red-200 bg-red-50 rounded-xl p-4">
              <h3 className="font-medium text-red-700 mb-2 flex items-center gap-1.5 text-sm">
                <AlertCircle size={16} /> Validation Errors
              </h3>
              <div className="max-h-48 overflow-y-auto space-y-1.5">
                {validationResults.errors.slice(0, 20).map((error, idx) => (
                  <div key={idx} className="text-sm text-red-600">
                    <span className="font-medium">Row {error.row}:</span> {error.message}
                  </div>
                ))}
                {validationResults.errors.length > 20 && (
                  <div className="text-sm text-gray-500 italic">... and {validationResults.errors.length - 20} more errors</div>
                )}
              </div>
            </div>
          )}

          {validationResults.valid_rows > 0 && (
            <div className="border border-green-200 bg-green-50 rounded-xl p-4">
              <h3 className="font-medium text-green-700 mb-1.5 flex items-center gap-1.5 text-sm">
                <CheckCircle size={16} /> Ready to Import
              </h3>
              <p className="text-sm text-gray-600">
                {validationResults.valid_rows} valid lead(s) will be imported.
                {validationResults.invalid_rows > 0 && ` ${validationResults.invalid_rows} invalid row(s) will be skipped.`}
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={() => setImportStep('upload')} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5">
              <ChevronLeft size={16} /> Back
            </button>
            <button onClick={handleClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button
              onClick={proceedWithImport}
              disabled={validationResults.valid_rows === 0 || importLoading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
            >
              {importLoading ? 'Importing...' : 'Import Leads'}
            </button>
          </div>
        </div>
      );
    }

    if (importStep === 'results' && importResults) {
      return (
        <div className="space-y-5">
          <div className="border border-green-200 bg-green-50 rounded-xl p-4">
            <h3 className="font-medium text-green-700 mb-2 flex items-center gap-1.5">
              <CheckCircle size={18} /> Import Complete
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              Successfully imported <strong className="text-green-700">{importResults.imported_count}</strong> lead(s).
            </p>
            {importResults.duplicate_count > 0 && (
              <p className="text-sm text-amber-600 mb-1"><strong>{importResults.duplicate_count}</strong> duplicate(s) skipped.</p>
            )}
            {importResults.skipped_count > 0 && (
              <p className="text-sm text-red-600"><strong>{importResults.skipped_count}</strong> row(s) failed.</p>
            )}
          </div>

          {importResults.error_report?.length > 0 && (
            <div className="border border-red-200 bg-red-50 rounded-xl p-4">
              <h3 className="font-medium text-red-700 mb-2 flex items-center gap-1.5 text-sm">
                <AlertCircle size={16} /> Failed Rows
              </h3>
              <div className="max-h-40 overflow-y-auto space-y-1.5 mb-3">
                {importResults.error_report.slice(0, 10).map((error, idx) => (
                  <div key={idx} className="text-sm text-red-600">
                    <span className="font-medium">Row {error.row}:</span> {error.message}
                  </div>
                ))}
                {importResults.error_report.length > 10 && (
                  <div className="text-sm text-gray-500 italic">... and {importResults.error_report.length - 10} more</div>
                )}
              </div>
              <button onClick={downloadErrorReport} className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                <Download size={14} /> Download Error Report
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{importResults.imported_count}</p>
              <p className="text-xs text-gray-500">Imported</p>
            </div>
            <div className="bg-red-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{importResults.skipped_count}</p>
              <p className="text-xs text-gray-500">Skipped</p>
            </div>
          </div>

          <button onClick={handleImportComplete} className="w-full px-4 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-black transition-colors">
            Close
          </button>
        </div>
      );
    }

    return null;
  };

  // ─── STEP 3: EXPORT FILTERS ────────────────────────────────
  const renderExport = () => {
    const guidance = getGuidance('export', source);
    const sourceFilter = SOURCE_FILTER_MAP[source];

    return (
      <div className="space-y-5">
        {/* Guidance */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 text-sm mb-2">Exporting {sourceFilter} leads</h3>
          <ul className="space-y-1">
            {guidance.map((item, i) => (
              <li key={i} className="flex gap-2 text-sm text-gray-700">
                <span className="text-blue-600 mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left: Filters */}
          <div className="lg:col-span-2 space-y-4">
            {/* Date Range */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-1.5">
                <Calendar size={15} /> Date Range
              </h4>
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { label: 'All Time', value: 'all' },
                  { label: 'Today', value: 'today' },
                  { label: 'Last 7 Days', value: '7days' },
                  { label: 'Last 30 Days', value: '30days' },
                  { label: 'Last Quarter', value: 'quarter' },
                  { label: 'Last Year', value: 'year' },
                ].map(preset => (
                  <button
                    key={preset.value}
                    onClick={() => applyDatePreset(preset.value)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
                      exportFilters.datePreset === preset.value
                        ? 'bg-gray-900 text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
                  <input type="date" value={exportFilters.dateFrom} onChange={(e) => setExportFilters({ ...exportFilters, dateFrom: e.target.value, datePreset: 'custom' })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
                  <input type="date" value={exportFilters.dateTo} onChange={(e) => setExportFilters({ ...exportFilters, dateTo: e.target.value, datePreset: 'custom' })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" />
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-1.5">
                <Filter size={15} /> Status
              </h4>
              <div className="flex flex-wrap gap-2">
                {statusOptions.map(status => (
                  <label key={status} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium cursor-pointer transition-colors ${
                    exportFilters.statuses.includes(status)
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                  }`}>
                    <input
                      type="checkbox"
                      checked={exportFilters.statuses.includes(status)}
                      onChange={(e) => {
                        if (e.target.checked) setExportFilters({ ...exportFilters, statuses: [...exportFilters.statuses, status] });
                        else setExportFilters({ ...exportFilters, statuses: exportFilters.statuses.filter(s => s !== status) });
                      }}
                      className="hidden"
                    />
                    {formatStatusLabel(status)}
                  </label>
                ))}
              </div>
            </div>

            {/* Source */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 text-sm mb-3">Source</h4>
              <p className="text-sm text-gray-500">Pre-selected: <span className="font-medium text-gray-900">{sourceFilter}</span></p>
            </div>

            {/* Location */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 text-sm mb-3">Location</h4>
              <input type="text" placeholder="Search locations..." value={exportFilters.locations} onChange={(e) => setExportFilters({ ...exportFilters, locations: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" />
            </div>

            {/* Budget */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 text-sm mb-3">Budget Range (PKR)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Min Budget</label>
                  <input type="number" placeholder="0" value={exportFilters.budgetMin} onChange={(e) => setExportFilters({ ...exportFilters, budgetMin: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Max Budget</label>
                  <input type="number" placeholder="∞" value={exportFilters.budgetMax} onChange={(e) => setExportFilters({ ...exportFilters, budgetMax: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" />
                </div>
              </div>
            </div>

            {/* Assigned To */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 text-sm mb-3">Assigned To</h4>
              <select value={exportFilters.assignedTo} onChange={(e) => setExportFilters({ ...exportFilters, assignedTo: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white">
                <option value="">All Users</option>
                <option value="unassigned">Unassigned</option>
                {assignableUsers.map(user => (
                  <option key={user.id} value={user.id}>{user.full_name || user.username}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Right: Preview & Options */}
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 text-sm mb-3">Preview</h4>
              <div className="text-3xl font-bold text-gray-900">{exportPreview.count}</div>
              <p className="text-xs text-gray-500 mt-1">Lead{exportPreview.count !== 1 ? 's' : ''} match your filters</p>
              {exportPreview.leads.length > 0 && (
                <div className="mt-3 space-y-1.5 max-h-40 overflow-y-auto">
                  <p className="text-xs font-medium text-gray-500">Sample:</p>
                  {exportPreview.leads.map(lead => (
                    <div key={lead.id} className="text-xs bg-white p-2 rounded-lg border border-blue-100">
                      <p className="font-medium text-gray-900">{lead.name}</p>
                      <p className="text-gray-500 truncate">{lead.email || lead.phone}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 text-sm mb-3">Export Format</h4>
              <div className="space-y-2">
                <label className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${exportOptions.format === 'csv' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 hover:border-gray-400'}`}>
                  <input type="radio" checked={exportOptions.format === 'csv'} onChange={() => setExportOptions({ ...exportOptions, format: 'csv' })} className="hidden" />
                  <span className="text-sm font-medium">CSV (.csv)</span>
                </label>
                <label className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${exportOptions.format === 'excel' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 hover:border-gray-400'}`}>
                  <input type="radio" checked={exportOptions.format === 'excel'} onChange={() => setExportOptions({ ...exportOptions, format: 'excel' })} className="hidden" />
                  <span className="text-sm font-medium">Excel (.xlsx)</span>
                </label>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 text-sm mb-3">Columns to Export</h4>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {EXPORT_FIELD_OPTIONS.map(field => (
                  <label key={field.value} className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer transition-colors ${exportOptions.includeFields.includes(field.value) ? 'bg-gray-100' : 'hover:bg-gray-100'}`}>
                    <input
                      type="checkbox"
                      checked={exportOptions.includeFields.includes(field.value)}
                      onChange={(e) => {
                        if (e.target.checked) setExportOptions({ ...exportOptions, includeFields: [...exportOptions.includeFields, field.value] });
                        else setExportOptions({ ...exportOptions, includeFields: exportOptions.includeFields.filter(f => f !== field.value) });
                      }}
                      className="w-3.5 h-3.5 accent-gray-900"
                    />
                    <span className="text-gray-700">{field.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Clear filters */}
            {(exportFilters.dateFrom || exportFilters.dateTo || exportFilters.statuses.length > 0 || exportFilters.locations || exportFilters.budgetMin || exportFilters.budgetMax || exportFilters.sources.length > 0 || exportFilters.assignedTo) && (
              <button onClick={() => setExportFilters({ dateFrom: '', dateTo: '', datePreset: 'all', statuses: [], locations: '', budgetMin: '', budgetMax: '', sources: [], assignedTo: '' })} className="w-full px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                Clear All Filters
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-200">
          <button onClick={() => setStep(2)} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5">
            <ChevronLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2">
            <button onClick={handleClose} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleExportClick}
              disabled={exportPreview.count === 0 || exportLoading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {exportLoading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              Export {exportPreview.count > 0 ? `${exportPreview.count} Lead${exportPreview.count !== 1 ? 's' : ''}` : 'Leads'}
            </button>
          </div>
        </div>

        {/* Export Confirmation Dialog */}
        {showExportConfirmation && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onClick={() => setShowExportConfirmation(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Confirm Export</h3>
                <button type="button" onClick={() => setShowExportConfirmation(false)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500">
                  <X size={18} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                  <div className="text-4xl font-bold text-gray-900 mb-1">{exportPreview.count}</div>
                  <p className="text-sm text-gray-500">Lead{exportPreview.count !== 1 ? 's' : ''} to export</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-500">Format:</span>
                    <span className="text-sm font-semibold text-gray-900 uppercase">{exportOptions.format === 'csv' ? 'CSV' : 'Excel'}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-500">Columns:</span>
                    <span className="text-sm font-semibold text-gray-900">{exportOptions.includeFields.length} selected</span>
                  </div>
                </div>
                {getAppliedFiltersSummary().length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Applied Filters</p>
                    <div className="space-y-1.5">
                      {getAppliedFiltersSummary().map((filter, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-xs text-gray-700">
                          <span className="inline-block w-1.5 h-1.5 bg-gray-900 rounded-full" /> {filter}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {getAppliedFiltersSummary().length === 0 && (
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-xs text-gray-500">No filters applied - exporting all your leads</p>
                  </div>
                )}
              </div>
              <div className="flex gap-3 p-5 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button onClick={() => setShowExportConfirmation(false)} className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button onClick={handleExport} disabled={exportLoading} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
                  {exportLoading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {exportLoading ? 'Exporting...' : 'Confirm Export'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── RENDER ────────────────────────────────────────────────
  const stepLabel = step === 1 ? 'Choose Action' : step === 2 ? 'Choose Source' : mode === 'import' ? 'Import Leads' : 'Export Leads';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{stepLabel}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {mode === 'import' ? 'Import' : 'Export'} · {SOURCE_OPTIONS.find(s => s.key === source)?.label || ''}
            </p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-700 transition-colors p-1">
            <X size={22} />
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-0">
            {STEPS.map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    step === s.num ? 'bg-gray-900 text-white' : step > s.num ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > s.num ? <CheckCircle size={14} /> : s.num}
                  </div>
                  <span className={`text-xs font-medium hidden sm:inline ${step === s.num ? 'text-gray-900' : 'text-gray-400'}`}>
                    {s.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-px mx-3 ${step > s.num ? 'bg-green-400' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && (mode === 'import' ? renderImport() : renderExport())}
        </div>
      </div>
    </div>
  );
};

export default DataTransferModal;
